/**
 * Realtime voice dictation session (issue #403).
 *
 * The `spec §` references below are docs/superpowers/specs/2026-08-06-dictation-ux-latency-design.md.
 *
 * Deliberately framework-agnostic: no AngularJS, no DOM access, and nothing
 * touched at module scope. Every browser capability it needs — fetch,
 * getUserMedia, RTCPeerConnection, timers, sendBeacon — arrives through
 * `deps`, which is what lets a spec drive the whole thing with a fake peer
 * connection and no browser at all. Same shape as the ai-chat modules, which
 * are raw-fetch code with the auth token provider registered from the Angular
 * side (see app-workspace/services/ai-chat/auth-fetch.ts).
 *
 * WHY THE BROWSER TALKS TO OPENAI DIRECTLY. Transcription has to appear as the
 * user speaks, and Azure Functions cannot hold a WebSocket, so relaying audio
 * through our own host would mean building it into the net48 web role. Instead
 * the server mints a short-lived, transcription-scoped secret and the browser
 * runs the media session itself. WebRTC (rather than a raw WebSocket) means we
 * write no audio code at all: no AudioWorklet, no 48k -> 24k resampling, no
 * Int16 packing, no jitter buffer.
 *
 * The cost of that choice is that usage is reported by the browser. It is
 * therefore recorded as a NON-BILLABLE product server-side — reported at real
 * cost, never charged — so an under-report is a reporting gap and not a
 * financial one. See src/GamePlanNodeJsFunctions/src/dictation/dictation.ts.
 */

/** Where a session is in its lifecycle. `error` is terminal for that attempt. */
export type DictationState =
    | "idle"
    | "requesting-permission"
    | "connecting"
    | "listening"
    | "stopping"
    | "error";

/**
 * Why a session failed, as a code rather than a message, so the UI owns the
 * wording and the module owns the diagnosis.
 */
export type DictationErrorCode =
    | "permission-denied"
    | "no-microphone"
    | "microphone-busy"
    | "forbidden"
    | "rate-limited"
    | "unavailable"
    | "connect-failed"
    | "network";

export interface DictationError {
    code: DictationErrorCode;
    /** Developer-facing detail. Never rendered verbatim to a user. */
    detail?: string;
    /** Only set for `rate-limited`. */
    retryAfterSeconds?: number;
}

/** Everything the mint endpoint hands back that a session needs to connect. */
export interface MintedSession {
    value: string;
    /** Unix seconds. The secret only OPENS a call while now < expiresAt. */
    expiresAt: number;
    ticket: string;
    model: string;
    selfSegments: boolean;
    prompt: string;
    /** True when this mint came from the surface-open prewarm cache. */
    prewarmed?: boolean;
}

export interface MintFailure {
    code: DictationErrorCode;
    detail?: string;
    retryAfterSeconds?: number;
}

export type MintResult = { ok: true; minted: MintedSession } | { ok: false; error: MintFailure };

export interface DictationCallbacks {
    /** Fired on every state change. */
    onState?: (state: DictationState) => void;
    /** In-flight text for the utterance being spoken. Replaces the previous interim. */
    onInterim?: (text: string) => void;
    /**
     * A settled chunk, as spoken, identified by `id`. Emitted the moment it
     * settles — BEFORE the cleanup pass runs — so the user's words are never
     * off the screen while a network call is in flight.
     */
    onSegment?: (id: number, text: string) => void;
    /**
     * The cleanup pass's version of segment `id`. Arrives up to a second later
     * and replaces that chunk in place. Carrying the id means a late response
     * can only ever affect the chunk it was asked about.
     */
    onSegmentRevised?: (id: number, text: string) => void;
    onError?: (error: DictationError) => void;
}

export interface DictationDeps {
    fetchImpl?: typeof fetch;
    getMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
    rtcFactory?: () => RTCPeerConnection;
    setTimeoutImpl?: (handler: () => void, timeout: number) => number;
    clearTimeoutImpl?: (handle: number) => void;
    sendBeaconImpl?: (url: string, data: string) => boolean;
    now?: () => number;
    /** Our mint endpoint. */
    sessionUrl?: string;
    /** Our usage-reporting endpoint. */
    usageUrl?: string;
    /** OpenAI's WebRTC call endpoint. Injectable so specs never reach the network. */
    callsUrl?: string;
    /** Our punctuation-cleanup endpoint. */
    tidyUrl?: string;
    /** Our connect-telemetry endpoint. */
    telemetryUrl?: string;
    /** Supplies a minted session — the service injects a prewarm-cache-aware
     *  provider. Defaults to minting fresh via `sessionUrl`. */
    getMint?: () => Promise<MintResult>;
    /** Permission probe for the parallel-start fast path. "unknown" (the
     *  default on any throw — Firefox rejects the "microphone" name) forces
     *  the safe sequential order. */
    queryMicPermission?: () => Promise<"granted" | "prompt" | "denied" | "unknown">;
}

export interface StartOptions {
    /** Attributed onto the usage row when dictation happens inside a plan. */
    planId?: string | null;
}

const DEFAULT_SESSION_URL = "/api/ai-chat/dictation/session";
const DEFAULT_USAGE_URL = "/api/ai-chat/dictation/usage";
const DEFAULT_CALLS_URL = "https://api.openai.com/v1/realtime/calls";
const DEFAULT_TIDY_URL = "/api/ai-chat/dictation/tidy";
const DEFAULT_TELEMETRY_URL = "/api/ai-chat/dictation/telemetry";

/** Floor: how long stop/finalize will wait for a final when NOTHING is
 *  buffered — the mainline accept ("…and the budget" → Enter 200ms later)
 *  arrives before the tail's deltas do, and a wait keyed only on buffered
 *  interim would resolve instantly and drop it (spec § B1). */
const FINALIZE_FLOOR_MS = 1500;

/** Cap: accept must never block longer than this. On expiry whatever is
 *  buffered is promoted raw. */
const FINALIZE_TIMEOUT_MS = 3000;

/** Periodic usage flush while a long session runs, so a crashed tab loses
 *  at most this much reporting rather than the whole session. */
const FLUSH_INTERVAL_MS = 30_000;

/** Give up if the data channel never opens. */
const CONNECT_TIMEOUT_MS = 15_000;

/**
 * Quiet period after the last word before an utterance is settled: sent through
 * the cleanup pass and committed as final text.
 *
 * Settling at a pause rather than only at the end means text tidies as the user
 * goes, at a natural boundary, instead of the whole field rewriting at once
 * when they stop. It also bounds how much is in flight if the tab dies.
 */
const IDLE_SETTLE_MS = 1000;

/**
 * Recognises a transcript that is really the session prompt coming back.
 *
 * These models treat `prompt` as prior context and will happily emit it as
 * though it had been spoken — a shipped construction glossary once landed in a
 * user's message box verbatim, after their real words. The prompt is our own
 * text and we know it exactly, so an echo is cheap to spot.
 *
 * Compares on letters and digits only, because the echo comes back
 * re-punctuated and re-cased. Uses containment rather than equality: the echo
 * often arrives as a fragment, or with a stray word attached.
 */
export function isPromptEcho(transcript: string, prompt: string): boolean {
    const normalise = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    const p = normalise(prompt);
    // A short prompt could legitimately be said out loud; only guard substantial ones.
    if (p.length < 40) return false;
    const t = normalise(transcript);
    if (t.length === 0) return false;

    // The transcript swallowed the whole prompt — unambiguous, any length.
    if (t.indexOf(p) !== -1) return true;
    // The transcript is a slice of the prompt. Require some length first, so a
    // genuine short utterance that happens to appear in the prompt survives.
    return t.length >= 24 && p.indexOf(t) !== -1;
}

/** Accumulated, in OpenAI's own usage shape so the server can hand it
 *  straight to normalizeUsage() without a bespoke parser. */
interface UsageAccumulator {
    inputTokens: number;
    outputTokens: number;
    audioTokens: number;
    textTokens: number;
    durationSeconds: number;
    /** True once anything at all has been added — an empty flush is skipped. */
    dirty: boolean;
}

function emptyUsage(): UsageAccumulator {
    return { inputTokens: 0, outputTokens: 0, audioTokens: 0, textTokens: 0, durationSeconds: 0, dirty: false };
}

function num(value: unknown): number {
    return typeof value === "number" && isFinite(value) && value > 0 ? value : 0;
}

/**
 * Whether this browser can dictate at all. Checked before the button renders —
 * a mic that cannot work should be absent, not disabled-with-a-tooltip.
 *
 * `isSecureContext` matters because getUserMedia is unavailable over plain
 * HTTP; localhost counts as secure, so local dev is fine.
 */
export function isDictationSupported(win: Window = window): boolean {
    const nav = win.navigator as Navigator | undefined;
    return !!(
        win.isSecureContext &&
        nav &&
        nav.mediaDevices &&
        typeof nav.mediaDevices.getUserMedia === "function" &&
        typeof (win as unknown as { RTCPeerConnection?: unknown }).RTCPeerConnection === "function"
    );
}

export interface DictationSession {
    start(options?: StartOptions): Promise<void>;
    stop(): Promise<void>;
    /**
     * Accept-driven stop: waits (floor 1.5s / cap 3s) for the transcript tail,
     * skips/cancels the tidy pass, resolves after the session is idle and all
     * raw transcript callbacks have been delivered. Idempotent.
     */
    finalize(): Promise<void>;
    /** Flush any pending usage without ending the session. Used on pagehide. */
    flushUsage(useBeacon?: boolean): void;
    readonly state: DictationState;
}

/**
 * Mint an ephemeral, transcription-scoped OpenAI secret via our server.
 * Module-level (not session-internal) so the DictationService prewarm cache
 * can mint with the same code path and error vocabulary (spec § A1).
 */
export async function mintDictationSession(
    fetchImpl: typeof fetch,
    sessionUrl: string,
): Promise<MintResult> {
    let response: Response;
    try {
        response = await fetchImpl(sessionUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: "{}",
            credentials: "same-origin",
        });
    } catch (err) {
        return { ok: false, error: { code: "network", detail: String(err) } };
    }

    if (response.status === 403) {
        return { ok: false, error: { code: "forbidden", detail: "dictation is not enabled for this account" } };
    }
    if (response.status === 429) {
        let retryAfter = 60;
        try {
            const body = (await response.json()) as { retryAfterSeconds?: number };
            if (typeof body?.retryAfterSeconds === "number") retryAfter = body.retryAfterSeconds;
        } catch {
            /* header/body absent — keep the default */
        }
        return {
            ok: false,
            error: { code: "rate-limited", detail: "too many dictation sessions", retryAfterSeconds: retryAfter },
        };
    }
    if (!response.ok) {
        return { ok: false, error: { code: "unavailable", detail: `mint failed: ${response.status}` } };
    }

    try {
        const body = (await response.json()) as {
            value?: string;
            expiresAt?: number;
            ticket?: string;
            model?: string;
            selfSegments?: boolean;
            prompt?: string;
        };
        if (!body?.value || !body?.ticket) {
            return { ok: false, error: { code: "unavailable", detail: "mint response incomplete" } };
        }
        return {
            ok: true,
            minted: {
                value: body.value,
                expiresAt: typeof body.expiresAt === "number" ? body.expiresAt : Date.now() / 1000 + 60,
                ticket: body.ticket,
                model: body.model ?? "",
                selfSegments: body.selfSegments === true,
                prompt: typeof body.prompt === "string" ? body.prompt : "",
            },
        };
    } catch (err) {
        return { ok: false, error: { code: "unavailable", detail: String(err) } };
    }
}

export function createDictationSession(
    callbacks: DictationCallbacks = {},
    deps: DictationDeps = {},
): DictationSession {
    const doFetch = deps.fetchImpl ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));
    const getMedia =
        deps.getMedia ?? ((constraints: MediaStreamConstraints) => navigator.mediaDevices.getUserMedia(constraints));
    const makePc = deps.rtcFactory ?? (() => new RTCPeerConnection());
    const setTimer = deps.setTimeoutImpl ?? ((h: () => void, t: number) => setTimeout(h, t) as unknown as number);
    const clearTimer = deps.clearTimeoutImpl ?? ((h: number) => clearTimeout(h));
    const sendBeacon =
        deps.sendBeaconImpl ??
        ((url: string, data: string) =>
            typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function"
                ? navigator.sendBeacon(url, new Blob([data], { type: "application/json" }))
                : false);
    const now = deps.now ?? (() => Date.now());
    const sessionUrl = deps.sessionUrl ?? DEFAULT_SESSION_URL;
    const usageUrl = deps.usageUrl ?? DEFAULT_USAGE_URL;
    const callsUrl = deps.callsUrl ?? DEFAULT_CALLS_URL;
    const tidyUrl = deps.tidyUrl ?? DEFAULT_TIDY_URL;
    const telemetryUrl = deps.telemetryUrl ?? DEFAULT_TELEMETRY_URL;
    const getMint = deps.getMint ?? (() => mintDictationSession(doFetch, sessionUrl));
    const queryMic =
        deps.queryMicPermission ??
        (async (): Promise<"granted" | "prompt" | "denied" | "unknown"> => {
            try {
                const status = await navigator.permissions.query({ name: "microphone" as PermissionName });
                return status.state;
            } catch {
                return "unknown";
            }
        });

    let state: DictationState = "idle";
    let pc: RTCPeerConnection | null = null;
    let channel: RTCDataChannel | null = null;
    let stream: MediaStream | null = null;
    let ticket: string | null = null;
    let model: string | null = null;
    let planId: string | null = null;
    /** True when the model segments utterances itself and only produces a final
     *  transcript once we commit the audio turn. See modelSelfSegments() server-side. */
    let selfSegments = false;
    /** The session prompt, as the server configured it. Empty unless
     *  DICTATION_PROMPT is set. Held only so an echo of it can be discarded. */
    let sessionPrompt = "";
    /** True once any chunk has been settled, so a late `.completed` knows the
     *  text is already accounted for rather than missing. */
    let settledSomething = false;
    /** Monotonic per session, so a cleanup response names exactly one chunk. */
    let nextSegmentId = 1;
    let usage = emptyUsage();
    let flushTimer: number | null = null;
    let connectTimer: number | null = null;
    let idleTimer: number | null = null;
    /** Bumped by every teardown. A start() continuation that awakes to find
     *  the epoch moved on has been superseded and must exit silently —
     *  otherwise it resumes into nulled state and surfaces a spurious
     *  connect-failed after a successful stop/finalize (spec § B1). */
    let epoch = 0;

    /** Interim text keyed by OpenAI item id. A Map (not a single string) so a
     *  delta for the next utterance arriving before the previous `.completed`
     *  cannot interleave into one buffer. */
    const interim = new Map<string, string>();

    /** In-flight stop/finalize, so a second caller joins rather than racing. */
    let stopping: Promise<void> | null = null;
    /** While true, settles skip the tidy round trip and pending revisions are
     *  dropped on arrival. Set by finalize(); reset by start(). */
    let suppressTidy = false;
    /** Hook the tail wait installs so settles/failures can release it. */
    let tailCheck: ((didSettle: boolean) => void) | null = null;

    /** Phase timings for the current start() attempt (spec § A4). */
    let connectStats: {
        started: number;
        micMs: number;
        mintMs: number;
        sdpMs: number;
        channelMs: number;
        prewarmed: boolean;
        sent: boolean;
    } | null = null;

    /** One fire-and-forget report per attempt, success or failure — the usage
     *  flush cannot carry failures (no ticket exists), and those are exactly
     *  the cold-start cases worth measuring. */
    function reportConnect(outcome: DictationErrorCode | "listening"): void {
        if (!connectStats || connectStats.sent) return;
        connectStats.sent = true;
        const body = JSON.stringify({
            outcome,
            prewarmed: connectStats.prewarmed,
            micMs: connectStats.micMs,
            mintMs: connectStats.mintMs,
            sdpMs: connectStats.sdpMs,
            channelMs: connectStats.channelMs,
            totalMs: now() - connectStats.started,
        });
        void doFetch(telemetryUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            credentials: "same-origin",
        }).catch(() => {
            /* telemetry is best-effort; never surface */
        });
    }

    function setState(next: DictationState): void {
        if (state === next) return;
        state = next;
        if (callbacks.onState) callbacks.onState(next);
    }

    function fail(code: DictationErrorCode, detail?: string, retryAfterSeconds?: number): void {
        // A failure mid-tail-wait must release the wait, never hang an accept.
        if (tailCheck) tailCheck(true);
        // Salvage before tearing down: if the network dropped mid-sentence, the
        // words already transcribed are still the user's and still wanted.
        promoteUnsettledInterim();
        setState("error");
        if (callbacks.onError) callbacks.onError({ code, detail, retryAfterSeconds });
        reportConnect(code);
        void teardown();
    }

    /**
     * The in-flight text worth showing: everything buffered, minus anything that
     * is the prompt echoing back. Filtered here rather than only at `.completed`
     * so the user never watches a glossary type itself into their field.
     */
    function visibleInterim(): string {
        let text = "";
        interim.forEach((value) => {
            if (!value) return;
            if (sessionPrompt && isPromptEcho(value, sessionPrompt)) return;
            text += (text ? " " : "") + value;
        });
        return text;
    }

    function emitInterim(): void {
        if (!callbacks.onInterim) return;
        callbacks.onInterim(visibleInterim());
    }

    function addUsage(raw: unknown): void {
        if (!raw || typeof raw !== "object") return;
        const u = raw as Record<string, unknown>;

        if (u.type === "duration") {
            const seconds = num(u.seconds);
            if (seconds > 0) {
                usage.durationSeconds += seconds;
                usage.dirty = true;
            }
            return;
        }

        const details = (u.input_token_details ?? u.input_tokens_details) as Record<string, unknown> | undefined;
        const input = num(u.input_tokens);
        const output = num(u.output_tokens);
        if (input === 0 && output === 0) return;

        usage.inputTokens += input;
        usage.outputTokens += output;
        usage.audioTokens += num(details?.audio_tokens);
        usage.textTokens += num(details?.text_tokens);
        usage.dirty = true;
    }

    /** Serialise the accumulator into the shape the server expects. */
    function usagePayload(): string | null {
        if (!usage.dirty || !ticket) return null;
        const body =
            usage.durationSeconds > 0
                ? { type: "duration", seconds: usage.durationSeconds }
                : {
                      type: "tokens",
                      input_tokens: usage.inputTokens,
                      output_tokens: usage.outputTokens,
                      total_tokens: usage.inputTokens + usage.outputTokens,
                      input_token_details: {
                          audio_tokens: usage.audioTokens,
                          text_tokens: usage.textTokens,
                      },
                  };
        return JSON.stringify({ ticket, model, planId, usage: body });
    }

    function flushUsage(useBeacon = false): void {
        const payload = usagePayload();
        if (!payload) return;

        // Reset before the request completes: a failed flush must not double-count
        // on the next one, and losing a flush is a reporting gap we have already
        // accepted (the usage is non-billable).
        usage = emptyUsage();

        if (useBeacon) {
            // sendBeacon cannot set an Authorization header; the server
            // authenticates this via the latestgameplantoken cookie fallback.
            sendBeacon(usageUrl, payload);
            return;
        }

        void doFetch(usageUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: payload,
            credentials: "same-origin",
        }).catch(() => {
            /* reporting is best-effort; never surface to the user */
        });
    }

    function startFlushTimer(): void {
        stopFlushTimer();
        flushTimer = setTimer(() => {
            flushUsage(false);
            if (state === "listening") startFlushTimer();
        }, FLUSH_INTERVAL_MS);
    }

    function stopFlushTimer(): void {
        if (flushTimer !== null) {
            clearTimer(flushTimer);
            flushTimer = null;
        }
    }

    function clearIdleTimer(): void {
        if (idleTimer !== null) {
            clearTimer(idleTimer);
            idleTimer = null;
        }
    }

    /** Restart the quiet-period countdown. Called on every delta. */
    function armIdleTimer(): void {
        clearIdleTimer();
        idleTimer = setTimer(() => {
            idleTimer = null;
            void settleInterim();
        }, IDLE_SETTLE_MS);
    }

    /**
     * Run a settled chunk through the cleanup pass. Falls back to the raw text
     * on any failure — a punctuation command that silently does not fire is a
     * mild annoyance; losing the words entirely is not.
     */
    async function tidy(text: string): Promise<string> {
        if (!ticket || !text.trim()) return text;
        try {
            const response = await doFetch(tidyUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ticket, text }),
                credentials: "same-origin",
            });
            if (!response.ok) return text;
            const body = (await response.json()) as { text?: string };
            return typeof body?.text === "string" && body.text.trim() ? body.text : text;
        } catch {
            return text;
        }
    }

    /**
     * Promote everything currently buffered into settled text, via the cleanup
     * pass. Clearing the buffer BEFORE awaiting matters: deltas that arrive
     * while the cleanup is in flight belong to the next chunk, and must not be
     * swallowed by this one.
     */
    async function settleInterim(): Promise<void> {
        const text = visibleInterim().trim();
        if (!text) return;
        interim.clear();
        settledSomething = true;

        // Publish the raw words FIRST. The cleanup is a round trip of roughly a
        // second, and an earlier version cleared the text before awaiting it —
        // so everything the user had just said vanished until the response
        // landed. The words now stay on screen throughout and are revised in
        // place when the cleaned version arrives. Raw publication also
        // releases any tail wait: it is finalize's join point (spec § B1).
        const id = nextSegmentId++;
        if (callbacks.onSegment) callbacks.onSegment(id, text);
        if (tailCheck) tailCheck(true);

        if (suppressTidy) return;
        const cleaned = await tidy(text);
        // finalize() may have begun while the round trip was in flight; a late
        // revision must not mutate a field whose contents were just submitted.
        if (suppressTidy) return;
        if (cleaned !== text && callbacks.onSegmentRevised) callbacks.onSegmentRevised(id, cleaned);
    }

    function handleServerEvent(raw: string): void {
        let event: Record<string, unknown>;
        try {
            event = JSON.parse(raw) as Record<string, unknown>;
        } catch {
            return;
        }

        const type = typeof event.type === "string" ? event.type : "";
        const itemId = typeof event.item_id === "string" ? event.item_id : "";

        if (type === "conversation.item.input_audio_transcription.delta") {
            const delta = typeof event.delta === "string" ? event.delta : "";
            if (!delta) return;
            interim.set(itemId, (interim.get(itemId) ?? "") + delta);
            emitInterim();
            armIdleTimer();
            return;
        }

        if (type === "conversation.item.input_audio_transcription.completed") {
            // Usage is recorded whatever the transcript turned out to contain —
            // OpenAI charged us for producing it.
            addUsage(event.usage);
            clearIdleTimer();

            // The event's transcript is the WHOLE turn, but idle-settling has
            // very likely committed most of it already. Re-emitting it here
            // would duplicate everything the user said. Settle only what is
            // still buffered instead — safe because concat(deltas) is exactly
            // the final transcript, measured on both models (dictation/probes/revision-probe.ts,
            // in GamePlanNodeJsFunctions).
            if (interim.size > 0) {
                void settleInterim();
                return;
            }

            // No deltas buffered. Either settling already took them, or this is
            // a model that emitted none — fall back to the event's own text so
            // an unmeasured model cannot silently produce nothing.
            if (settledSomething) return;
            const transcript = typeof event.transcript === "string" ? event.transcript.trim() : "";
            if (!transcript) return;
            if (sessionPrompt && isPromptEcho(transcript, sessionPrompt)) return;
            if (callbacks.onSegment) callbacks.onSegment(nextSegmentId++, transcript);
            // This direct emit is a settle too — release any tail wait.
            if (tailCheck) tailCheck(true);
            return;
        }

        if (type === "conversation.item.input_audio_transcription.failed") {
            // One utterance failed to transcribe. The session is still healthy —
            // drop the interim for it and keep listening rather than tearing down.
            interim.delete(itemId);
            emitInterim();
            // A failed item settles nothing, but it may have emptied the
            // buffer — a tail wait must not burn its cap on a dead utterance.
            if (tailCheck) tailCheck(false);
            return;
        }

        if (type === "error") {
            if (state === "stopping") {
                // Mid-accept the server objecting (typically to a commit on an
                // already-committed buffer) means nothing more is coming.
                // Release the wait; never surface an error toast here.
                if (tailCheck) tailCheck(true);
                return;
            }
            const err = event.error as { message?: string } | undefined;
            fail("connect-failed", err?.message ?? "realtime error");
        }
    }

    async function start(options: StartOptions = {}): Promise<void> {
        if (state !== "idle" && state !== "error") return;

        planId = options.planId ?? null;
        usage = emptyUsage();
        interim.clear();
        settledSomething = false;
        suppressTidy = false;
        nextSegmentId = 1;
        const myEpoch = epoch;
        connectStats = { started: now(), micMs: 0, mintMs: 0, sdpMs: 0, channelMs: 0, prewarmed: false, sent: false };

        // Microphone permission BEFORE minting: a denied prompt must not burn a
        // mint against the user's rate limit. Parallel only when permission is
        // already granted — the one state where no prompt can appear
        // (spec § A2). Residual accepted edge: granted + device-busy burns one
        // mint.
        setState("requesting-permission");
        const canParallel = (await queryMic()) === "granted";
        if (epoch !== myEpoch) return;

        const mintEarly = canParallel ? getMint() : null;
        if (mintEarly) {
            // Failures are read from the result below; a rejection while
            // getUserMedia is still pending must not go unhandled.
            mintEarly.catch(() => undefined);
        }

        let media: MediaStream;
        try {
            media = await getMedia({
                audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
        } catch (err) {
            // The media arm failed. The early mint (if any) is simply dropped;
            // the MEDIA error is the one the user hears about (spec § A2).
            const name = (err as { name?: string } | null)?.name ?? "";
            if (name === "NotAllowedError" || name === "SecurityError") fail("permission-denied", name);
            else if (name === "NotFoundError" || name === "OverconstrainedError") fail("no-microphone", name);
            else if (name === "NotReadableError") fail("microphone-busy", name);
            else fail("no-microphone", String(err));
            return;
        }
        if (epoch !== myEpoch) {
            // Superseded while the permission prompt or device picker was up:
            // never leave the microphone live behind a dead attempt.
            media.getTracks().forEach((t) => {
                try {
                    t.stop();
                } catch {
                    /* already stopped */
                }
            });
            return;
        }
        stream = media;
        if (connectStats) connectStats.micMs = now() - connectStats.started;

        setState("connecting");
        let result = await (mintEarly ?? getMint());
        if (epoch !== myEpoch) return; // teardown already stopped the tracks
        if (!result.ok) {
            // fail() tears down, which stops the mic tracks assigned above.
            fail(result.error.code, result.error.detail, result.error.retryAfterSeconds);
            return;
        }

        // Freshness re-check (spec § A2): a slow device picker or an old
        // prewarm can leave the secret near expiry, and the SDP exchange
        // would 401 with a misleading network-blame error. Re-mint instead.
        if (result.minted.expiresAt * 1000 - now() < 5000) {
            result = await getMint();
            if (epoch !== myEpoch) return;
            if (!result.ok) {
                fail(result.error.code, result.error.detail, result.error.retryAfterSeconds);
                return;
            }
        }
        const minted = result.minted;
        if (connectStats) {
            connectStats.mintMs = now() - connectStats.started;
            connectStats.prewarmed = minted.prewarmed === true;
        }
        ticket = minted.ticket;
        model = minted.model;
        selfSegments = minted.selfSegments;
        sessionPrompt = minted.prompt;

        try {
            pc = makePc();
            channel = pc.createDataChannel("oai-events");
            channel.onmessage = (event: MessageEvent) => handleServerEvent(String(event.data));
            channel.onopen = () => {
                if (connectTimer !== null) {
                    clearTimer(connectTimer);
                    connectTimer = null;
                }
                if (connectStats) connectStats.channelMs = now() - connectStats.started;
                setState("listening");
                startFlushTimer();
                reportConnect("listening");
            };

            pc.oniceconnectionstatechange = () => {
                const ice = pc?.iceConnectionState;
                // "failed" is the signal that UDP/STUN could not get through —
                // the realistic failure on a locked-down corporate network.
                if (ice === "failed" && state !== "stopping" && state !== "idle") {
                    fail("connect-failed", "ICE failed — the network may block WebRTC");
                }
            };

            const track = stream.getTracks()[0];
            if (track) pc.addTrack(track, stream);

            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            if (epoch !== myEpoch) return;

            const sdpResponse = await doFetch(callsUrl, {
                method: "POST",
                body: offer.sdp ?? "",
                headers: {
                    Authorization: `Bearer ${minted.value}`,
                    "Content-Type": "application/sdp",
                },
            });
            if (epoch !== myEpoch) return;

            if (!sdpResponse.ok) {
                fail("connect-failed", `sdp exchange failed: ${sdpResponse.status}`);
                return;
            }

            await pc.setRemoteDescription({ type: "answer", sdp: await sdpResponse.text() });
            if (epoch !== myEpoch) return;
            if (connectStats) connectStats.sdpMs = now() - connectStats.started;

            connectTimer = setTimer(() => {
                if (state === "connecting") fail("connect-failed", "data channel never opened");
            }, CONNECT_TIMEOUT_MS);
        } catch (err) {
            // A teardown mid-await makes the resumed code throw on closed
            // objects; that is supersession, not a connect failure.
            if (epoch !== myEpoch) return;
            fail("connect-failed", String(err));
        }
    }

    async function teardown(): Promise<void> {
        epoch++;
        stopFlushTimer();
        clearIdleTimer();
        if (connectTimer !== null) {
            clearTimer(connectTimer);
            connectTimer = null;
        }
        try {
            if (channel) channel.close();
        } catch {
            /* already closed */
        }
        try {
            if (pc) pc.close();
        } catch {
            /* already closed */
        }
        if (stream) {
            stream.getTracks().forEach((t) => {
                try {
                    t.stop();
                } catch {
                    /* already stopped */
                }
            });
        }
        channel = null;
        pc = null;
        stream = null;
        interim.clear();
    }

    /**
     * Event-driven wait for the transcript tail (spec § B1): resolves when the
     * interim buffer is empty AND (a settle has happened since the wait began
     * OR the floor has elapsed), capped at FINALIZE_TIMEOUT_MS. The floor
     * exists because the mainline accept arrives before the tail's deltas do —
     * a wait keyed only on buffered interim would resolve instantly and drop
     * the last words. A settle at 800ms resolves at 800ms, faster than the
     * old fixed grace.
     */
    function waitForTail(): Promise<void> {
        return new Promise<void>((resolve) => {
            let settled = false;
            let floorElapsed = false;
            let done = false;
            const timers: number[] = [];
            const finish = () => {
                if (done) return;
                done = true;
                tailCheck = null;
                timers.forEach((t) => clearTimer(t));
                resolve();
            };
            const check = () => {
                if (interim.size === 0 && (settled || floorElapsed)) finish();
            };
            tailCheck = (didSettle: boolean) => {
                if (didSettle) settled = true;
                check();
            };
            timers.push(setTimer(() => { floorElapsed = true; check(); }, FINALIZE_FLOOR_MS));
            timers.push(setTimer(finish, FINALIZE_TIMEOUT_MS));
        });
    }

    async function doStop(skipTidy: boolean): Promise<void> {
        if (state === "requesting-permission" || state === "connecting") {
            // Abort the connect: no deltas can have arrived before the data
            // channel opened, so there is nothing recoverable to wait for.
            // teardown's epoch bump makes the suspended start() exit silently.
            await teardown();
            setState("idle");
            return;
        }

        const wasListening = state === "listening";
        setState("stopping");
        if (skipTidy) suppressTidy = true;

        if (wasListening && channel && channel.readyState === "open") {
            // A self-segmenting model only produces its final transcript once
            // the audio turn is committed, so ask for that before waiting.
            // Conditional, as before: committing an empty buffer on a
            // server-VAD session draws an error event.
            if (selfSegments) {
                try {
                    channel.send(JSON.stringify({ type: "input_audio_buffer.commit" }));
                } catch {
                    /* channel already closing — the tail wait below still applies */
                }
            }
            await waitForTail();
        }

        // Anything still buffered never settled — the commit above may have
        // been too late, or the model may not emit finals at all. It is
        // nonetheless text the user actually spoke, so publish it rather than
        // discard it — losing dictated words is the worst failure this feature
        // can have. The tidy round trip is deliberately NOT awaited: the raw
        // words publish synchronously and the revision trails in exactly as it
        // does at pause boundaries.
        clearIdleTimer();
        void settleInterim();

        await teardown();
        flushUsage(false);
        ticket = null;
        setState("idle");
    }

    function stop(): Promise<void> {
        if (state === "idle") return Promise.resolve();
        if (stopping) return stopping;
        const run = doStop(false).finally(() => {
            if (stopping === run) stopping = null;
        });
        stopping = run;
        return run;
    }

    /**
     * Accept-driven stop (spec § B1): same tail wait as stop(), but the tidy
     * pass is suppressed for the rest of this attempt — at accept the raw
     * words are what gets submitted, and a late revision must not mutate a
     * field whose contents were just sent. Idempotent; joins an in-flight
     * mic-click stop at raw-segment publication.
     */
    function finalize(): Promise<void> {
        if (stopping) {
            suppressTidy = true;
            return stopping;
        }
        if (state === "idle" || state === "error") return Promise.resolve();
        suppressTidy = true;
        const run = doStop(true).finally(() => {
            if (stopping === run) stopping = null;
        });
        stopping = run;
        return run;
    }

    /** Emit any un-settled interim text as a segment, so it reaches the field.
     *  Used on failure, where there is no time for a cleanup round trip — the
     *  raw words matter far more than their punctuation. */
    function promoteUnsettledInterim(): void {
        if (interim.size === 0) return;
        const text = visibleInterim().trim(); // prompt echoes already excluded
        interim.clear();
        if (callbacks.onInterim) callbacks.onInterim("");
        if (text && callbacks.onSegment) callbacks.onSegment(nextSegmentId++, text);
    }

    return {
        start,
        stop,
        finalize,
        flushUsage,
        get state() {
            return state;
        },
    };
}

/** Elapsed-time constants shared with specs and any session-cap UI. */
export function dictationLimits(): {
    finalizeFloorMs: number;
    finalizeTimeoutMs: number;
    flushIntervalMs: number;
    connectTimeoutMs: number;
} {
    return {
        finalizeFloorMs: FINALIZE_FLOOR_MS,
        finalizeTimeoutMs: FINALIZE_TIMEOUT_MS,
        flushIntervalMs: FLUSH_INTERVAL_MS,
        connectTimeoutMs: CONNECT_TIMEOUT_MS,
    };
}
